<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import {
  Building2,
  Cake,
  ChevronDown,
  ClipboardCheck,
  ExternalLink,
  FileText,
  FolderKanban,
  GraduationCap,
  History,
  Home,
  LayoutDashboard,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Network,
  Search,
  ShieldCheck,
  Siren,
  Sun,
  TreePalm,
  User,
  UserCog,
  Users,
  UsersRound,
  Waypoints,
  X,
} from 'lucide-vue-next';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterLink, useRoute } from 'vue-router';

import type { AssignmentSummaryDto } from '@/api/generated/models/index.js';
import type { Component } from 'vue';


import { assignmentsControllerSummaryV1 } from '@/api/generated/assignments/assignments.js';
import CommandPalette from '@/components/CommandPalette.vue';
import NotificationBell from '@/components/NotificationBell.vue';
import { useTheme, type ThemePref } from '@/composables/useTheme.js';
import { useAuthStore } from '@/stores/auth.js';

interface NavLink {
  to: { name: string };
  label: string;
  icon: Component;
  /** Счётчик на иконке (например, сколько поручений ждут меня). */
  badge?: number;
}
interface NavGroup {
  key: string;
  label: string;
  links: NavLink[];
}

const { t } = useI18n();
const authStore = useAuthStore();

// Сколько поручений ждут моего шага — счётчик на пункте «Поручения».
const { data: assignmentsSummary } = useQuery({
  queryKey: ['assignments', 'summary'],
  queryFn: () => assignmentsControllerSummaryV1() as unknown as Promise<AssignmentSummaryDto>,
  enabled: computed(() => authStore.isAuthenticated),
  refetchInterval: 120_000,
});
const assignmentsLink = computed<NavLink>(() => ({
  to: { name: 'assignments' },
  label: t('nav.assignments'),
  icon: ClipboardCheck,
  badge: assignmentsSummary.value?.tabs.wait || undefined,
}));

const navGroups = computed<NavGroup[]>(() => {
  const groups: NavGroup[] = [
    {
      key: 'work',
      label: t('navGroups.work'),
      links: [assignmentsLink.value],
    },
    {
      key: 'people',
      label: t('navGroups.people'),
      links: [
        { to: { name: 'employees' }, label: t('nav.employees'), icon: Users },
        { to: { name: 'org-chart' }, label: t('nav.orgChart'), icon: Network },
        { to: { name: 'birthdays' }, label: t('nav.birthdays'), icon: Cake },
      ],
    },
    {
      key: 'services',
      label: t('navGroups.services'),
      links: [
        { to: { name: 'projects' }, label: t('nav.projects'), icon: FolderKanban },
        { to: { name: 'vacations' }, label: t('nav.vacations'), icon: TreePalm },
        { to: { name: 'documents' }, label: t('nav.documents'), icon: FileText },
        { to: { name: 'emergency' }, label: t('nav.emergency'), icon: Siren },
        { to: { name: 'onboarding' }, label: t('nav.onboarding'), icon: GraduationCap },
        { to: { name: 'infrastructure' }, label: t('nav.infrastructure'), icon: Waypoints },
      ],
    },
  ];
  if (authStore.isAdmin) {
    groups.push({
      key: 'admin',
      label: t('navGroups.admin'),
      links: [
        { to: { name: 'admin-overview' }, label: t('nav.adminOverview'), icon: LayoutDashboard },
        { to: { name: 'admin-access' }, label: t('nav.adminAccess'), icon: ShieldCheck },
        { to: { name: 'admin-employees' }, label: t('nav.adminEmployees'), icon: UserCog },
        { to: { name: 'admin-departments' }, label: t('nav.adminDepartments'), icon: Building2 },
        { to: { name: 'admin-report-proxies' }, label: t('nav.adminReportProxies'), icon: UsersRound },
        { to: { name: 'admin-audit' }, label: t('nav.adminAudit'), icon: History },
      ],
    });
  }
  return groups;
});

/** Главные разделы для нижнего таб-бара на мобильном (+ кнопка «Ещё»). */
const primaryTabs = computed<NavLink[]>(() => [
  { to: { name: 'home' }, label: t('nav.home'), icon: Home },
  assignmentsLink.value,
  { to: { name: 'employees' }, label: t('nav.employees'), icon: Users },
  { to: { name: 'projects' }, label: t('nav.projects'), icon: FolderKanban },
]);

// Командная палитра (⌘K) — глобальный слушатель живёт внутри компонента.
const paletteOpen = ref(false);

// Тема: системная по умолчанию, выбор пользователя перевешивает и сохраняется.
const { pref: themePref } = useTheme();
const themeOptions = computed<{ value: ThemePref; label: string; icon: Component }[]>(() => [
  { value: 'system', label: t('shell.themeSystem'), icon: Monitor },
  { value: 'light', label: t('shell.themeLight'), icon: Sun },
  { value: 'dark', label: t('shell.themeDark'), icon: Moon },
]);

// Шит «Ещё» (полное меню на мобильном).
const moreOpen = ref(false);
const route = useRoute();
watch(
  () => route.fullPath,
  () => {
    moreOpen.value = false;
  },
);

const lkUrl = import.meta.env.VITE_LK_URL ?? 'http://localhost:5173';

const userInitials = computed(() => {
  const name = authStore.user?.displayName ?? authStore.user?.email ?? '';
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  return (name[0] ?? '?').toUpperCase();
});

const menuOpen = ref(false);
const menuRef = ref<HTMLElement | null>(null);

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}
function closeMenu() {
  menuOpen.value = false;
}
function handleLogout() {
  closeMenu();
  void authStore.logout();
}

function handleDocumentClick(event: MouseEvent) {
  if (!menuOpen.value) return;
  const target = event.target as Node | null;
  if (menuRef.value && target && !menuRef.value.contains(target)) {
    closeMenu();
  }
}
function handleKey(event: KeyboardEvent) {
  if (event.key === 'Escape') closeMenu();
}

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleKey);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleKey);
});
</script>

<template>
  <div class="shell">
    <aside class="rail">
      <RouterLink to="/" class="rail__brand" :aria-label="t('app.name')">
        <svg class="rail__logo" viewBox="0 0 32 32" aria-hidden="true">
          <defs>
            <linearGradient id="intranet-rail-logo"
x1="0"
y1="0"
x2="32"
y2="32"
gradientUnits="userSpaceOnUse">
              <stop offset="0%" stop-color="#3B4CBD" />
              <stop offset="100%" stop-color="#6366F1" />
            </linearGradient>
          </defs>
          <rect width="32"
height="32"
rx="8"
fill="url(#intranet-rail-logo)" />
          <path
            d="M16 7 L24 11 V21 L16 25 L8 21 V11 Z"
            stroke="#FFFFFF"
            stroke-width="2"
            stroke-linejoin="round"
            fill="none"
          />
        </svg>
      </RouterLink>

      <nav class="rail__nav">
        <template v-for="(group, gi) in navGroups" :key="group.key">
          <div v-if="gi > 0" class="rail__sep" role="separator" />
          <RouterLink
            v-for="link in group.links"
            :key="link.to.name"
            :to="link.to"
            class="rail__item"
            active-class="rail__item--active"
            :aria-label="link.label"
            :title="link.label"
          >
            <component :is="link.icon" :size="20" :stroke-width="1.75" />
            <span v-if="link.badge" class="rail__badge">{{ link.badge > 99 ? '99+' : link.badge }}</span>
            <span class="rail__tip">{{ link.label }}</span>
          </RouterLink>
        </template>
      </nav>
    </aside>

    <div class="shell__body">
      <header class="topbar">
        <span class="topbar__title">{{ t('app.name') }}</span>

        <div class="topbar__actions">
          <button type="button" class="topbar__search" @click="paletteOpen = true">
            <Search :size="14" :stroke-width="1.75" />
            <span class="topbar__search-label">{{ t('palette.trigger') }}</span>
            <kbd class="topbar__kbd">⌘K</kbd>
          </button>

          <a class="topbar__lk-link" :href="lkUrl">
            <ExternalLink :size="14" :stroke-width="1.75" />
            <span>{{ t('shell.toLk') }}</span>
          </a>

          <NotificationBell />
          <div ref="menuRef" class="user-menu">
            <button
              type="button"
              class="user-trigger"
              :aria-expanded="menuOpen"
              aria-haspopup="menu"
              @click="toggleMenu"
            >
              <span class="user-avatar">{{ userInitials }}</span>
              <span class="user-meta">
                <span class="user-name">{{ authStore.user?.displayName ?? '—' }}</span>
                <span class="user-email">{{ authStore.user?.email ?? '' }}</span>
              </span>
              <ChevronDown :size="16" :stroke-width="1.75" class="user-caret" />
            </button>

            <div v-if="menuOpen" class="user-dropdown" role="menu">
              <RouterLink to="/"
class="user-dropdown__item"
role="menuitem"
@click="closeMenu">
                <User :size="16" :stroke-width="1.75" />
                <span>{{ t('shell.profile') }}</span>
              </RouterLink>
              <div class="user-dropdown__sep" role="separator" />

              <div class="theme-row">
                <span class="theme-row__label">{{ t('shell.theme') }}</span>
                <div class="theme-seg" role="group" :aria-label="t('shell.theme')">
                  <button
                    v-for="opt in themeOptions"
                    :key="opt.value"
                    type="button"
                    class="theme-seg__btn"
                    :class="{ 'theme-seg__btn--on': themePref === opt.value }"
                    :aria-pressed="themePref === opt.value"
                    :title="opt.label"
                    @click="themePref = opt.value"
                  >
                    <component :is="opt.icon" :size="15" :stroke-width="1.75" />
                  </button>
                </div>
              </div>

              <div class="user-dropdown__sep" role="separator" />
              <button
                type="button"
                class="user-dropdown__item user-dropdown__item--danger"
                role="menuitem"
                @click="handleLogout"
              >
                <LogOut :size="16" :stroke-width="1.75" />
                <span>{{ t('shell.logout') }}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main class="content">
        <slot />
      </main>
    </div>

    <!-- Нижний таб-бар (только мобильный) -->
    <nav class="tabbar" :aria-label="t('shell.menu')">
      <RouterLink
        v-for="tab in primaryTabs"
        :key="tab.to.name"
        :to="tab.to"
        class="tabbar__item"
        active-class="tabbar__item--active"
      >
        <span class="tabbar__icon">
          <component :is="tab.icon" :size="22" :stroke-width="1.75" />
          <span v-if="tab.badge" class="rail__badge">{{ tab.badge > 99 ? '99+' : tab.badge }}</span>
        </span>
        <span class="tabbar__label">{{ tab.label }}</span>
      </RouterLink>
      <button
        type="button"
        class="tabbar__item tabbar__more"
        :class="{ 'tabbar__item--active': moreOpen }"
        :aria-expanded="moreOpen"
        @click="moreOpen = true"
      >
        <Menu :size="22" :stroke-width="1.75" />
        <span class="tabbar__label">{{ t('shell.more') }}</span>
      </button>
    </nav>

    <!-- Шит «Ещё»: полное меню на мобильном -->
    <Transition name="sheet">
      <div v-if="moreOpen" class="sheet-overlay" @click.self="moreOpen = false">
        <div class="sheet" role="dialog" aria-modal="true">
          <span class="sheet__grip" aria-hidden="true" />
          <header class="sheet__head">
            <span class="sheet__title">{{ t('shell.menu') }}</span>
            <button
              type="button"
              class="sheet__close"
              :aria-label="t('common.close')"
              @click="moreOpen = false"
            >
              <X :size="20" :stroke-width="2" />
            </button>
          </header>
          <div class="sheet__body">
            <div v-for="group in navGroups" :key="group.key" class="sheet__group">
              <span class="sheet__group-label">{{ group.label }}</span>
              <RouterLink
                v-for="link in group.links"
                :key="link.to.name"
                :to="link.to"
                class="sheet__link"
                active-class="sheet__link--active"
              >
                <component :is="link.icon" :size="20" :stroke-width="1.75" />
                <span>{{ link.label }}</span>
                <span v-if="link.badge" class="sheet__badge">{{ link.badge }}</span>
              </RouterLink>
            </div>
            <div class="sheet__group">
              <a class="sheet__link" :href="lkUrl">
                <ExternalLink :size="20" :stroke-width="1.75" />
                <span>{{ t('shell.toLk') }}</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <CommandPalette v-model:open="paletteOpen" />
  </div>
</template>

<style scoped>
.shell {
  display: flex;
  min-height: 100vh;
  background: var(--color-bg-surface);
}

/* ---- Левый иконочный rail ---- */
.rail {
  position: sticky;
  top: 0;
  align-self: flex-start;
  height: 100vh;
  width: 72px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--color-bg);
  border-right: 1px solid var(--color-border-subtle);
  z-index: 30;
}

.rail__brand {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 56px;
  width: 100%;
}
.rail__logo {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.rail__nav {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  width: 100%;
  padding: var(--space-2) 0;
}

.rail__sep {
  width: 28px;
  height: 1px;
  background: var(--color-border-subtle);
  margin: var(--space-2) 0;
}

.rail__item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-md);
  color: var(--color-text-muted);
  text-decoration: none;
  transition: background var(--transition-fast), color var(--transition-fast);
}
.rail__item:hover {
  background: var(--color-bg-soft);
  color: var(--color-text);
  text-decoration: none;
}
.rail__item--active {
  background: var(--color-primary-soft);
  color: var(--color-primary);
}

/* Счётчик на иконке пункта меню */
.rail__badge {
  position: absolute;
  top: 3px;
  right: 2px;
  display: grid;
  place-items: center;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border: 2px solid var(--color-bg);
  border-radius: var(--radius-full);
  background: var(--color-danger);
  color: #fff;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}
.tabbar__icon {
  position: relative;
  display: inline-flex;
}
.tabbar__icon .rail__badge {
  top: -6px;
  right: -10px;
}
.sheet__badge {
  margin-left: auto;
  min-width: 20px;
  padding: 1px 6px;
  border-radius: var(--radius-full);
  background: var(--color-danger);
  color: #fff;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  text-align: center;
}

/* Всплывающая подпись справа от иконки */
.rail__tip {
  position: absolute;
  left: calc(100% + 10px);
  top: 50%;
  transform: translateY(-50%) translateX(-4px);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  /* Инвертированная плашка: фон — цвет текста, текст — цвет фона. Работает
     в обеих темах без хардкода белого. */
  background: var(--color-text);
  color: var(--color-bg);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  box-shadow: var(--shadow-md);
  transition: opacity var(--transition-fast), transform var(--transition-fast);
  z-index: var(--z-dropdown);
}
.rail__item:hover .rail__tip {
  opacity: 1;
  transform: translateY(-50%) translateX(0);
}

/* ---- Тело: топбар + контент ---- */
.shell__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  height: 56px;
  padding: 0 var(--space-5);
  background: var(--color-bg-translucent);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--color-border-subtle);
}

.topbar__title {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-md);
  letter-spacing: var(--letter-spacing-tight);
  color: var(--color-text);
}

.topbar__actions {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.topbar__search {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: 32px;
  padding: 0 var(--space-2) 0 var(--space-3);
  min-width: 210px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-surface);
  color: var(--color-text-placeholder);
  font-family: inherit;
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: border-color var(--transition-fast), color var(--transition-fast);
}
.topbar__search:hover {
  border-color: var(--color-primary);
  color: var(--color-text-secondary);
}
.topbar__search-label {
  flex: 1;
  text-align: left;
}
.topbar__kbd {
  font-family: var(--font-family-mono);
  font-size: 10px;
  background: var(--color-bg-soft);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
  padding: 2px 5px;
  color: var(--color-text-muted);
}

.topbar__lk-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  transition: background var(--transition-fast), color var(--transition-fast);
}
.topbar__lk-link:hover {
  background: var(--color-bg-soft);
  color: var(--color-text);
  text-decoration: none;
}

.user-menu {
  position: relative;
}
.user-trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  padding: 4px 8px 4px 4px;
  border: 1px solid transparent;
  background: transparent;
  border-radius: var(--radius-full);
  cursor: pointer;
  color: var(--color-text);
  transition: background var(--transition-fast), border-color var(--transition-fast);
}
.user-trigger:hover {
  background: var(--color-bg-soft);
  border-color: var(--color-border-subtle);
}
.user-trigger[aria-expanded='true'] {
  background: var(--color-bg-soft);
  border-color: var(--color-border);
}

.user-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
  background: var(--color-primary-soft);
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
}

.user-meta {
  display: none;
  flex-direction: column;
  text-align: left;
  max-width: 180px;
}
.user-name {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-email {
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-caret {
  display: none;
  color: var(--color-text-muted);
}

@media (min-width: 720px) {
  .user-meta,
  .user-caret {
    display: inline-flex;
  }
}

.user-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  padding: var(--space-1);
  z-index: var(--z-dropdown);
}
.user-dropdown__item {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  width: 100%;
  background: transparent;
  border: none;
  text-align: left;
  font-family: inherit;
  font-size: var(--font-size-base);
  color: var(--color-text);
  text-decoration: none;
  cursor: pointer;
  transition: background var(--transition-fast), color var(--transition-fast);
}
.user-dropdown__item:hover {
  background: var(--color-bg-soft);
  color: var(--color-text);
  text-decoration: none;
}
.user-dropdown__item--danger {
  color: var(--color-danger);
}
.user-dropdown__item--danger:hover {
  background: var(--color-danger-soft);
  color: var(--color-danger-hover);
}
.user-dropdown__sep {
  height: 1px;
  margin: var(--space-1) var(--space-1);
  background: var(--color-border-subtle);
}

/* Тема — сегментированный переключатель прямо в меню пользователя */
.theme-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
}
.theme-row__label {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}
.theme-seg {
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  background: var(--color-bg-soft);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
}
.theme-seg__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 24px;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: background var(--transition-fast), color var(--transition-fast);
}
.theme-seg__btn:hover {
  color: var(--color-text);
}
.theme-seg__btn--on {
  background: var(--color-bg);
  color: var(--color-primary);
  box-shadow: var(--shadow-xs);
}

.content {
  flex: 1;
  padding: var(--space-8) var(--space-8);
  max-width: 1600px;
  width: 100%;
  margin: 0 auto;
  min-width: 0;
}

/* ---- Мобильный таб-бар и шит «Ещё» (на десктопе скрыты) ---- */
.tabbar {
  display: none;
}
.sheet-overlay {
  display: none;
}

@media (max-width: 720px) {
  .rail {
    display: none;
  }
  .content {
    padding: var(--space-5) var(--space-4);
    /* место под фиксированный таб-бар + safe-area «чёлки» */
    padding-bottom: calc(60px + env(safe-area-inset-bottom) + var(--space-4));
  }
  .topbar {
    padding: 0 var(--space-4);
  }
  /* Поиск схлопывается в иконку, ссылка в ЛК прячется — место под заголовок */
  .topbar__search {
    min-width: 0;
    padding: 0 var(--space-2);
  }
  .topbar__search-label,
  .topbar__kbd {
    display: none;
  }
  .topbar__lk-link span {
    display: none;
  }

  .tabbar {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 40;
    background: var(--color-bg);
    border-top: 1px solid var(--color-border-subtle);
    padding-bottom: env(safe-area-inset-bottom);
  }
  .tabbar__item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    min-height: 56px;
    padding: var(--space-2) 0;
    background: transparent;
    border: none;
    color: var(--color-text-muted);
    text-decoration: none;
    font-family: inherit;
    cursor: pointer;
    transition: color var(--transition-fast);
  }
  .tabbar__item--active {
    color: var(--color-primary);
  }
  .tabbar__label {
    font-size: 11px;
    font-weight: var(--font-weight-medium);
    line-height: 1;
  }

  .sheet-overlay {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 50;
    background: rgba(0, 0, 0, 0.4);
  }
  .sheet {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    max-height: 85vh;
    display: flex;
    flex-direction: column;
    background: var(--color-bg);
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    padding: var(--space-2) var(--space-4) calc(var(--space-4) + env(safe-area-inset-bottom));
    box-shadow: var(--shadow-xl);
  }
  .sheet__grip {
    width: 40px;
    height: 4px;
    border-radius: var(--radius-full);
    background: var(--color-border);
    margin: var(--space-1) auto var(--space-2);
  }
  .sheet__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--space-1);
  }
  .sheet__title {
    font-weight: var(--font-weight-semibold);
    font-size: var(--font-size-md);
    color: var(--color-text);
  }
  .sheet__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    background: transparent;
    color: var(--color-text-muted);
    border-radius: var(--radius-md);
    cursor: pointer;
  }
  .sheet__close:hover {
    background: var(--color-bg-soft);
  }
  .sheet__body {
    overflow-y: auto;
  }
  .sheet__group {
    display: flex;
    flex-direction: column;
    padding: var(--space-2) 0;
  }
  .sheet__group + .sheet__group {
    border-top: 1px solid var(--color-border-subtle);
  }
  .sheet__group-label {
    font-size: var(--font-size-xs);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--color-text-muted);
    font-weight: var(--font-weight-semibold);
    padding: 0 var(--space-2) var(--space-1);
  }
  .sheet__link {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    min-height: 48px;
    padding: 0 var(--space-2);
    border-radius: var(--radius-md);
    color: var(--color-text);
    text-decoration: none;
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-medium);
  }
  .sheet__link--active {
    background: var(--color-primary-soft);
    color: var(--color-primary);
  }
}

/* Анимация шита «Ещё» */
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity var(--transition-base);
}
.sheet-enter-active .sheet,
.sheet-leave-active .sheet {
  transition: transform var(--transition-base);
}
.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}
.sheet-enter-from .sheet,
.sheet-leave-to .sheet {
  transform: translateY(100%);
}
</style>
